import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ReportSummaryItemApi } from "../api/Reports";
import {
  WORKFLOW_MANUAL_SCRAPE_STATUS,
  WORKFLOW_STEP_STATUS_ENUM,
} from "../api/Workflows";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import { Modal } from "../shared-components/Modal";
import { useActorInfoQuery } from "../api/UsersApi";
import { isActorUuidMatch } from "../api/Users";
import { useLockModal, useReportOriginManager } from "./ReportsHooks";
import { isReportLockedByOther } from "./utils";
import { AxiosError } from "axios";
import LoadingSpinner from "../shared-components/LoadingSpinner";

type ModalStateProps = {
  report: ReportSummaryItemApi;
  isOpen: boolean;
  toggleOpen: (isOpen: boolean) => void;
  onLockFailed?: (workflowRunUUID: string) => void;
  forceLocked?: boolean;
};

/** Step routes that have a parallel ``/amendment/`` route registered. QA routes (aqa/iqa/cqa) do not. */
const AMENDMENT_STEP_ROUTES = new Set([
  "detail",
  "post-edit",
  "post-rate",
  "system",
  "scrape",
  "article-edit",
]);

/** Route template from the clicked step's workflow step config, when it is an app route. */
function selectedStepUiLink(report: ReportSummaryItemApi): string | undefined {
  const link = (
    report as ReportSummaryItemApi & { selected_step_ui_link?: string | null }
  ).selected_step_ui_link;
  return link?.startsWith("/") ? link : undefined;
}

function isValidReportState(report: ReportSummaryItemApi): boolean {
  if (selectedStepUiLink(report)) return true;

  if (
    WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape &&
    report.step_payload.MANUAL_SCRAPE_READY ===
      WORKFLOW_MANUAL_SCRAPE_STATUS.IN_PROGRESS
  )
    return true;

  switch (report.step_name) {
    case WORKFLOW_STEP_STATUS_ENUM.PostsManualRate:
    case WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate:
    case WORKFLOW_STEP_STATUS_ENUM.WebContentReview:
    case WORKFLOW_STEP_STATUS_ENUM.ArticlesManualEdit:
    case WORKFLOW_STEP_STATUS_ENUM.PostsManualEdit:
    case WORKFLOW_STEP_STATUS_ENUM.AmendmentQA:
    case WORKFLOW_STEP_STATUS_ENUM.InternalQA:
    case WORKFLOW_STEP_STATUS_ENUM.ClientQA:
    case WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend:
      return true;
  }
  return false;
}

const ReportNavigationModal = ({
  report,
  isOpen,
  toggleOpen,
  onLockFailed,
  forceLocked = false,
}: ModalStateProps) => {
  const navigate = useNavigate();

  const {
    isLoadingWorkflowLock,
    lock,
    lockedWorkflowRun,
    errorWorkflowLock,
    resetWorkflowLock,
  } = useLockModal();
  const { setOrigin } = useReportOriginManager();
  const { data: actorInfo, isLoading: isActorInfoLoading } =
    useActorInfoQuery(true);
  const actorUuid = actorInfo?.actor_uuid;
  const lockedByOther = isReportLockedByOther(report, actorUuid);
  const isReadOnly =
    lockedByOther ||
    (forceLocked && !isActorUuidMatch(actorUuid, report.locked_by));
  const isValidReport = isValidReportState(report);
  // Until actor identity resolves we can't tell a self-held lock from a
  // foreign one, so defer the read-only/auto-lock decision to avoid a
  // misclassified flash.
  const showReadOnly = isValidReport && isReadOnly && !isActorInfoLoading;
  const info = { title: "", text: "", isValid: "" };

  const navigateReportPage = useCallback(
    (currentReport: ReportSummaryItemApi, navigationIsReadOnly: boolean) => {
      const amendment = currentReport.was_amend ? "/amendment" : "";

      function navigateWithOrigin(url: string) {
        setOrigin("in_process");
        navigate(url, {
          state: {
            report: currentReport,
            isReadOnly: navigationIsReadOnly,
          },
        });
      }

      const stepUiLink = selectedStepUiLink(currentReport);
      if (stepUiLink) {
        const reportPrefix = `/reports/${currentReport.report_uuid}/`;
        let route = stepUiLink.replace(
          "{report_uuid}",
          currentReport.report_uuid
        );
        if (currentReport.was_amend && route.startsWith(reportPrefix)) {
          const suffix = route.slice(reportPrefix.length);
          if (AMENDMENT_STEP_ROUTES.has(suffix)) {
            route = `${reportPrefix}amendment/${suffix}`;
          }
        }
        navigateWithOrigin(route);
        return;
      }

      if (
        currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.WebContentReview
      ) {
        navigateWithOrigin(
          `/reports/${currentReport.report_uuid}${amendment}/webcontent`
        );
        return;
      }

      if (
        currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate
      ) {
        navigateWithOrigin(
          `/reports/${currentReport.report_uuid}${amendment}/detail`
        );
        return;
      }

      if (
        currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualRate
      ) {
        navigateWithOrigin(
          `/reports/${currentReport.report_uuid}${amendment}/post-rate`
        );
        return;
      }

      if (
        currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualEdit
      ) {
        navigateWithOrigin(
          `/reports/${currentReport.report_uuid}${amendment}/post-edit`
        );
        return;
      }

      if (
        currentReport.step_name ===
          WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape &&
        currentReport.step_payload.MANUAL_SCRAPE_READY ===
          WORKFLOW_MANUAL_SCRAPE_STATUS.IN_PROGRESS
      ) {
        navigateWithOrigin(
          `/reports/${currentReport.report_uuid}${amendment}/scrape`
        );
        return;
      }

      if (
        currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.ArticlesManualEdit
      ) {
        navigateWithOrigin(
          `/reports/${currentReport.report_uuid}${amendment}/article-edit`
        );
        return;
      }

      if (currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.AmendmentQA) {
        navigateWithOrigin(`/reports/${currentReport.report_uuid}/aqa`);
        return;
      }

      if (currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.InternalQA) {
        navigateWithOrigin(`/reports/${currentReport.report_uuid}/iqa`);
        return;
      }

      if (currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.ClientQA) {
        navigateWithOrigin(`/reports/${currentReport.report_uuid}/cqa`);
        return;
      }

      if (
        currentReport.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend
      ) {
        setOrigin("in_process");
        navigate(`/360/${currentReport.report_uuid}/workbench`, {
          state: {
            amendment360: true,
            report: currentReport,
            isReadOnly: navigationIsReadOnly,
          },
        });
        return;
      }
    },
    [navigate, setOrigin]
  );

  const navigateToValidReportPageAfterLock = useCallback(() => {
    navigateReportPage(report, false);
  }, [navigateReportPage, report]);

  useEffect(() => {
    if (
      isOpen &&
      isValidReport &&
      !isActorInfoLoading &&
      !isLoadingWorkflowLock &&
      !lockedWorkflowRun &&
      !isReadOnly
    ) {
      lock(report.workflow_run_uuid, "in_process").catch(() => {});
    }
  }, [
    isOpen,
    isValidReport,
    isActorInfoLoading,
    lock,
    report.workflow_run_uuid,
    isLoadingWorkflowLock,
    lockedWorkflowRun,
    report,
    isReadOnly,
  ]);

  useEffect(() => {
    if (!errorWorkflowLock) return;

    const error = errorWorkflowLock as AxiosError;
    const status = error?.response?.status;

    if (status === 409 && onLockFailed) {
      onLockFailed(report.workflow_run_uuid);
    }

    resetWorkflowLock();
  }, [
    errorWorkflowLock,
    resetWorkflowLock,
    onLockFailed,
    report.workflow_run_uuid,
  ]);

  useEffect(() => {
    if (lockedWorkflowRun) {
      navigateToValidReportPageAfterLock();
    }
  }, [lockedWorkflowRun, navigateToValidReportPageAfterLock]);

  if (!isValidReport) {
    info.title = "Report in Invalid State";
    info.text = `Report with Id: ${report.report_uuid} has an invalid state or cannot be loaded.`;
  }

  if (showReadOnly) {
    info.title = "Report is locked";
    info.text = "If you continue the report will be in a Read Only State";
  }

  function closeModal() {
    toggleOpen?.(false);
  }

  return (
    <>
      {(!isValidReport || showReadOnly) && (
        <Modal
          open={isOpen}
          onClose={closeModal}
          title={info.title}
          width={"w-full max-w-lg"}
        >
          <p className="py-6">{info.text}</p>
          <div className="flex gap-4 justify-end">
            <Button
              text={isValidReport ? "Cancel" : "Close"}
              colorType={ButtonColorTypes.cancel}
              onClick={closeModal}
            />
            {isValidReport && (
              <Button
                text="Continue"
                colorType={ButtonColorTypes.primary}
                onClick={() => navigateReportPage(report, isReadOnly)}
              />
            )}
          </div>
        </Modal>
      )}
      {(isLoadingWorkflowLock || (isValidReport && isActorInfoLoading)) && (
        <LoadingSpinner />
      )}
    </>
  );
};

export default ReportNavigationModal;
